import React, { useEffect, useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Grid, Typography, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import MyTable from '../utils/MyTable';

import moment from 'moment';

import { useDispatch } from 'react-redux';
import allActions from '../../actions';

import UnoButton from '../utils/UnoButton';
import { TemporaryTransferAPI } from '../../API/temporary_transfer';
import { getColorSuitability } from '../../controllers/ClientController';
import { classificationsMap } from '../main/backOffice/NewClientScreen';

import { unoTheme } from '../../assets/styles/unoTheme';

function getStatusChipStyle(status) {
    switch (status) {
        case 'ATIVA':
            return { background: unoTheme.greenMoney, color: 'white' };
        case 'CONCLUIDA':
            return { background: unoTheme.mainGrayColor, color: '#616161' };
        case 'AGENDADA':
        default:
            return { background: unoTheme.warningColor, color: '#5A4B00' };
    }
}

const useStyles = makeStyles((theme) => ({
    dialogTitle: {
        '& h2': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
        },
    },
    statusChip: {
        display: 'inline-block',
        padding: '2px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        textTransform: 'uppercase',
    },
    contentModal: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        paddingBottom: theme.spacing(2),
    },
    infoGrid: {
        marginBottom: theme.spacing(2),
    },
    infoLabel: {
        fontSize: '12px',
        color: unoTheme.secondaryColor,
        textTransform: 'uppercase',
    },
    infoValue: {
        fontSize: '15px',
        color: '#1C2228',
        fontWeight: 500,
    },
    tableTitle: {
        fontSize: '14px',
        fontWeight: 'bold',
        color: '#1C2228',
        marginBottom: theme.spacing(1),
    },
    loaderWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(4),
        '& .MuiCircularProgress-root': {
            color: unoTheme.mainColor,
        },
    },
    classificationChip: {
        borderRadius: '5px',
    },
    actionsFlex: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    confirmDeleteText: {
        marginRight: 'auto',
        color: unoTheme.errorColor,
        fontSize: '14px',
    },
}));

function InfoItem({ label, value, classes }) {
    return (
        <Grid item xs={6}>
            <Typography className={classes.infoLabel}>{label}</Typography>
            <Typography className={classes.infoValue}>{value}</Typography>
        </Grid>
    );
}

export default function DetailTemporaryTransferModal(props) {

    const { open, handleAction, transfer } = props;

    const classes = useStyles();
    const dispatch = useDispatch();

    const [clients, setClients] = useState([]);
    const [loader, setLoader] = useState(false);
    const [confirmingDelete, setConfirmingDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);

    async function doDelete() {
        setDeleting(true);
        const response = await TemporaryTransferAPI.deleteTemporaryTransfer(transfer.id);
        setDeleting(false);

        if (response.success) {
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                type: 'success',
                message: 'Agendamento excluído com sucesso!',
            }));
            setConfirmingDelete(false);
            handleAction(true); // fecha e sinaliza atualização da lista
        } else {
            dispatch(allActions.mainAlertActions.setMainAlert({
                show: true,
                type: 'error',
                message: 'Erro ao excluir o agendamento: ' + (response.body && response.body.message ? response.body.message : ''),
            }));
        }
    }

    async function doGetClients(transferId) {
        setLoader(true);
        const response = await TemporaryTransferAPI.getTemporaryTransferClients(transferId);
        if (response.success && response.body) {
            setClients(response.body.rows || []);
        } else {
            console.error("RESPONSE ERROR: ", response.body);
            setClients([]);
        }
        setLoader(false);
    }

    useEffect(() => {
        setConfirmingDelete(false);
        if (open && transfer && transfer.id) {
            doGetClients(transfer.id);
        } else {
            setClients([]);
        }
    }, [open, transfer]);

    if (!transfer) {
        return null;
    }

    const periodo = moment.utc(transfer.period_start).format('DD/MM/YYYY')
        + ' - '
        + moment.utc(transfer.period_end).format('DD/MM/YYYY');

    return (
        <Dialog
            fullWidth={true}
            maxWidth={'md'}
            open={open}
            onClose={() => handleAction(false)}>

            <DialogTitle className={classes.dialogTitle}>
                {'Transferência temporária'}
                <span
                    className={classes.statusChip}
                    style={getStatusChipStyle(transfer.status)}>
                    {transfer.status}
                </span>
            </DialogTitle>

            <DialogContent className={classes.contentModal}>

                <Grid container spacing={2} className={classes.infoGrid}>
                    <InfoItem label={'Período'} value={periodo} classes={classes} />
                    <InfoItem label={'Criado por'} value={transfer.created_by_name || '-'} classes={classes} />
                    <InfoItem label={'Consultor de origem'} value={transfer.origin_name || '-'} classes={classes} />
                    <InfoItem label={'Consultor destino (temporário)'} value={transfer.destination_name || '-'} classes={classes} />
                </Grid>

                <Typography className={classes.tableTitle}>
                    {`Clientes (${transfer.num_clients != null ? transfer.num_clients : clients.length})`}
                </Typography>

                {loader ?
                    <div className={classes.loaderWrapper}>
                        <CircularProgress disableShrink />
                    </div>
                    :
                    <MyTable
                        stickyHeader
                        nofixed={true}
                        size="small">
                        <colgroup>
                            <col width="44%" />
                            <col width="12%" />
                            <col width="16%" />
                            <col width="14%" />
                            <col width="14%" />
                        </colgroup>
                        <TableHead>
                            <TableRow>
                                <TableCell align="left">RPPS</TableCell>
                                <TableCell align="center">Class.</TableCell>
                                <TableCell align="center">Suitability</TableCell>
                                <TableCell align="center">Início carteira</TableCell>
                                <TableCell align="center">Fim carteira</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {clients.map((row) => (
                                <TableRow key={'detail-transfer-client-' + row.id}>
                                    <TableCell align="left">{row.label_name}</TableCell>
                                    <TableCell align="center">
                                        <div
                                            className={classes.classificationChip}
                                            style={{ background: classificationsMap[row.classification]?.color || classificationsMap[null].color }}>
                                            {row.classification || '-'}
                                        </div>
                                    </TableCell>
                                    <TableCell align="center" style={{ color: getColorSuitability(row.suitability) }}>
                                        {row.suitability ? row.suitability : '-'}
                                    </TableCell>
                                    <TableCell align="center">{moment.utc(row.portfolio_init).format('MM/YYYY')}</TableCell>
                                    <TableCell align="center">{row.portfolio_closed ? row.portfolio_closed : '-'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </MyTable>
                }

            </DialogContent>

            <DialogActions className={classes.actionsFlex}>
                {confirmingDelete ?
                    <>
                        <Typography className={classes.confirmDeleteText}>
                            {'Excluir este agendamento?'}
                        </Typography>
                        <UnoButton
                            disabled={deleting}
                            margin={'mRight'}
                            onClick={() => setConfirmingDelete(false)}>
                            {'Cancelar'}
                        </UnoButton>
                        <UnoButton
                            disabled={deleting}
                            type="error"
                            onClick={() => doDelete()}>
                            {'Confirmar exclusão'}
                        </UnoButton>
                    </>
                    :
                    <>
                        {transfer.status === 'AGENDADA' ?
                            <UnoButton type="errorClean" onClick={() => setConfirmingDelete(true)}>
                                {'Excluir'}
                            </UnoButton>
                            : <span />}
                        <UnoButton type="primary" onClick={() => handleAction(false)}>
                            {'Fechar'}
                        </UnoButton>
                    </>
                }
            </DialogActions>

        </Dialog>
    );
}
